import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState, useEffect } from "react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { PosterCard } from "@/components/rawan/PosterCard";
import { useAllItems } from "@/lib/library-store";
import { useTheme } from "@/lib/theme-context";
import { smartSearch, type SmartSearchHit } from "@/lib/ai-search.functions";
import { Sparkles, Loader2 } from "lucide-react";

type Search = { q?: string };

export const Route = createFileRoute("/search")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    q: typeof s.q === "string" ? s.q : undefined,
  }),
  loaderDeps: ({ search }) => ({ q: search.q ?? "" }),
  loader: ({ deps }) => ({ q: deps.q }),
  head: ({ loaderData }) => {
    const q = loaderData?.q?.trim();
    const title = q ? `بحث: ${q} — Rawan UI` : "بحث — Rawan UI";
    return {
      meta: [
        { title },
        { name: "description", content: "ابحث في مكتبة الأفلام والمسلسلات." },
        { property: "og:title", content: title },
        { property: "og:url", content: q ? `/search?q=${encodeURIComponent(q)}` : "/search" },
        { name: "robots", content: "noindex,follow" },
      ],
      links: [{ rel: "canonical", href: "/search" }],
    };
  },
  component: SearchPage,
});

function normalize(s: string): string {
  return (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u064B-\u0652]/g, "") // Arabic diacritics
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .trim();
}

function SearchPage() {
  const { q: initial } = Route.useSearch();
  const navigate = useNavigate({ from: "/search" });
  const { language } = useTheme();
  const all = useAllItems();
  const [query, setQuery] = useState(initial ?? "");
  const runSmartSearch = useServerFn(smartSearch);
  const [aiHits, setAiHits] = useState<SmartSearchHit[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    setQuery(initial ?? "");
    setAiHits(null);
    setAiError(null);
  }, [initial]);

  const t = (ar: string, en: string, fr: string) =>
    language === "ar" ? ar : language === "fr" ? fr : en;

  const doSmartSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await runSmartSearch({ data: { query: q, limit: 12 } });
      setAiHits(res.hits);
    } catch {
      setAiError(t("تعذّر البحث الذكي.", "Smart search failed.", "Échec de la recherche IA."));
    } finally {
      setAiLoading(false);
    }
  };

  const results = useMemo(() => {
    const nq = normalize(query);
    if (!nq) return [];
    const tokens = nq.split(/\s+/).filter(Boolean);
    return all.filter((m) => {
      const hay = normalize(
        [m.title, m.titleEn, m.titleFr, ...(m.genres || []), ...(m.genresEn || []), ...(m.genresFr || []), m.director]
          .filter(Boolean)
          .join(" "),
      );
      return tokens.every((tok) => hay.includes(tok));
    });
  }, [all, query]);

  const movies = results.filter((r) => r.type === "movie");
  const series = results.filter((r) => r.type === "series");

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold mb-4">
          {t("البحث", "Search", "Recherche")}
        </h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ search: { q: query.trim() || undefined }, replace: true });
          }}
          className="mb-6 flex gap-2"
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("ابحث عن فيلم أو مسلسل…", "Search movies or series…", "Rechercher un film ou une série…")}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-2 outline-none focus:ring-2 focus:ring-primary"
            autoFocus
          />
          <button type="submit" className="btn-primary px-5 py-2 rounded-lg">
            {t("بحث", "Search", "OK")}
          </button>
          <button
            type="button"
            onClick={doSmartSearch}
            disabled={aiLoading || !query.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 disabled:opacity-60"
            title={t("بحث بالذكاء الاصطناعي", "AI-powered search", "Recherche IA")}
          >
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {t("ذكي", "AI", "IA")}
          </button>
        </form>
        {aiError && <p className="mb-4 text-sm text-destructive">{aiError}</p>}
        {aiHits && aiHits.length > 0 && (
          <section className="mb-8 rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-bold text-primary">
                {t("نتائج البحث الذكي", "Smart results", "Résultats IA")}
              </h2>
            </div>
            <ul className="space-y-2">
              {aiHits.map((h) => (
                <li key={`${h.kind}-${h.slug}`}>
                  <Link
                    to={h.kind === "movie" ? "/movie/$slug" : "/series/$slug"}
                    params={{ slug: h.slug }}
                    className="block rounded-lg bg-card border border-border p-3 hover:border-primary/60"
                  >
                    <div className="text-sm font-semibold">
                      {h.title}{" "}
                      <span className="text-xs text-muted-foreground">
                        {h.kind === "movie" ? "فيلم" : "مسلسل"}
                        {h.year ? ` · ${h.year}` : ""}
                      </span>
                    </div>
                    {h.reason && <p className="mt-1 text-xs text-foreground/70">{h.reason}</p>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!query.trim() ? (
          <p className="text-muted-foreground">
            {t("اكتب اسم فيلم أو مسلسل أو تصنيف…", "Type a movie, series or genre…", "Tapez un titre ou un genre…")}
          </p>
        ) : results.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-border rounded-2xl bg-card">
            <p className="text-base font-semibold mb-1">
              {t("لا توجد نتائج مطابقة.", "No matching results.", "Aucun résultat.")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("جرّب كلمات مختلفة أو تصفّح", "Try different words or browse", "Essayez d'autres mots ou parcourez")}{" "}
              <Link to="/categories" className="underline">
                {t("التصنيفات", "categories", "les catégories")}
              </Link>
              .
            </p>
          </div>
        ) : (
          <>
            {movies.length > 0 && (
              <section className="mb-10">
                <h2 className="text-xl font-bold mb-3">
                  {t("الأفلام", "Movies", "Films")} ({movies.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
                  {movies.map((m) => <PosterCard key={m.id} movie={m} />)}
                </div>
              </section>
            )}
            {series.length > 0 && (
              <section>
                <h2 className="text-xl font-bold mb-3">
                  {t("المسلسلات", "Series", "Séries")} ({series.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
                  {series.map((m) => <PosterCard key={m.id} movie={m} />)}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
